import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { api, money } from "../lib/api";
import { PageHeader } from "../components/common";
import { PlaybookCards } from "./Landing";

const GTM = ["Week 1: setup call. Connect the payment processor and platform APIs, and deploy the consent modal and evidence collection.",
  "Week 2: historical dispute analysis. See which past chargebacks would have been won.",
  "Week 3: go live. Monitor and personally win the first 3–5 disputes.",
  "Week 4+: automate. AI representments run on staged approval, with monthly check-ins."];
const GUIDES = [
  ["1 · Consent proof", "Capture layered consent at checkout: identity confirmation, signature, and explicit acceptance of non-refundable terms. Hash and timestamp the exact terms text. This beats 'I didn't authorize this' (fraud reason codes)."],
  ["2 · Evidence collection", "Log every login, module view, download, attendance and certificate as it happens, signed and hash-chained before any dispute exists. Delivery proof beats 'not received'."],
  ["3 · Representment language", "Open with a one-line case summary, map each piece of evidence to the reason code's requirement, cite timestamps and IPs, and close with a specific reversal request. Keep it factual; issuers skim."],
];
const hit = (q, ...parts) => !q || parts.join(" ").toLowerCase().includes(q);

const Section = ({ title, children, testid, show = true }) => show && (
  <section className="mt-10" data-testid={testid}><p className="label-xs text-brand mb-4">{title}</p>{children}</section>
);

export const PlaybookContent = () => {
  const [k, setK] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/playbook/knowledge").then((r) => setK(r.data)); }, []);
  const s = q.trim().toLowerCase();
  const reasons = useMemo(() => (k?.reasons || []).filter((r) => hit(s, r.label, Object.values(r.codes).join(" "), r.required.join(" "))), [k, s]);
  const banks = useMemo(() => (k?.banks || []).filter((b) => hit(s, b.name, b.channel, (b.rewards || []).join(" "))), [k, s]);
  const examples = useMemo(() => (k?.examples || []).filter((e) => hit(s, e.reason, e.issuer, e.evidence.join(" "))), [k, s]);
  const guides = GUIDES.filter(([t, b]) => hit(s, t, b));
  const empty = s && !reasons.length && !banks.length && !examples.length && !guides.length;
  return (
    <div data-testid="playbook-content">
      <label className="flex items-center gap-3 border border-white/15 px-4 py-3 focus-within:border-brand transition-colors max-w-2xl">
        <Search className="w-4 h-4 text-white/40" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder='Search: "4855", "Chase", "device fingerprint", "subscription"…' className="flex-1 bg-transparent text-sm focus:outline-none" data-testid="playbook-search" />
      </label>
      {empty && <p className="text-sm text-white/45 mt-6" data-testid="playbook-no-results">No matches. Try a reason code (e.g. 10.4, 4855), a bank, or an evidence type.</p>}
      <Section title="How to maximize your win rate" testid="pb-guides" show={guides.length > 0}>
        <div className="grid md:grid-cols-3 gap-4">{guides.map(([t, b]) => <div key={t} className="border border-white/10 p-5"><h3 className="font-bold">{t}</h3><p className="text-sm text-white/65 mt-2">{b}</p></div>)}</div>
      </Section>
      <Section title="Reason code strategies" testid="pb-reasons" show={reasons.length > 0}>
        <div className="grid md:grid-cols-2 gap-4">{reasons.map((r) => (
          <div key={r.key} className="border border-white/10 p-5" data-testid={`pb-reason-${r.key}`}>
            <p className="font-mono text-[10px] text-[#FF2BD6]">VISA {r.codes.visa} · MC {r.codes.mastercard} · AMEX {r.codes.amex}</p>
            <h3 className="font-bold mt-1">{r.label}</h3>
            <p className="text-sm text-white/65 mt-2">Lead with <b className="text-white">{r.required[0]}</b>, then back it with {r.required.slice(1).join(", ")}. Missing any of these is the most common reason these disputes are lost.</p>
          </div>))}</div>
      </Section>
      <Section title="Bank-specific tactics · from your dispute history" testid="pb-banks" show={banks.length > 0}>
        <div className="grid md:grid-cols-3 gap-4">{banks.map((b) => (
          <div key={b.name} className="border border-white/10 p-5" data-testid={`pb-bank-${b.name.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="flex justify-between"><h3 className="font-bold">{b.name}</h3><span className="font-mono text-brand">{b.win_rate}%</span></div>
            <p className="text-sm text-white/65 mt-2">Prefers <b className="text-white">{b.channel}</b>. Rewards: {(b.rewards || []).join(", ") || "not enough history yet"}.</p>
          </div>))}</div>
      </Section>
      <Section title="Real examples · anonymized disputes that won" testid="pb-examples" show={examples.length > 0}>
        <div className="grid md:grid-cols-2 gap-4">{examples.map((e) => (
          <div key={e.id} className="border border-brand/20 bg-brand/[0.03] p-5" data-testid={`pb-example-${e.id}`}>
            <p className="font-mono text-[10px] text-brand">WON · {money(e.amount)} · {e.issuer}</p>
            <h3 className="font-bold mt-1">{e.reason}</h3>
            <p className="text-sm text-white/65 mt-2">Evidence used: {e.evidence.join(", ")}.</p>
          </div>))}</div>
      </Section>
      {!s && <>
        <Section title="Playbooks by client type" testid="pb-client-types"><PlaybookCards /></Section>
        <div className="mt-8 border border-white/10 p-6" data-testid="gtm-sequence">
          <p className="label-xs">Onboarding sequence · pricing: 15% of recovered funds, $0 if you lose, no monthly minimum</p>
          <ol className="mt-3 space-y-2">{GTM.map((g) => <li key={g} className="text-sm text-white/75">{g}</li>)}</ol>
        </div>
        <p className="text-xs text-white/35 mt-4">Client-type figures are illustrative ranges, not guarantees.</p>
      </>}
    </div>
  );
};

export default function Playbook() {
  return (
    <div data-testid="playbook-page">
      <PageHeader eyebrow="// official playbook · tos enforcement" title="The AEGIS Playbook" />
      <PlaybookContent />
    </div>
  );
}

export function PublicPlaybook() {
  return (
    <div className="min-h-screen bg-canvas text-white grain" data-testid="public-playbook-page">
      <nav className="flex items-center justify-between px-6 md:px-12 py-4 border-b border-white/10">
        <Link to="/" className="flex items-center gap-3" data-testid="pp-logo-link"><img src="/logo.webp" alt="AEGIS Guard logo" className="w-11 h-11 shrink-0 object-contain" /><span className="font-mono font-extrabold tracking-[0.3em]">AEGIS <span className="text-[#7DD3FC]">GUARD</span></span></Link>
        <Link to="/login" className="font-mono text-xs px-4 py-2 bg-brand text-black font-bold" data-testid="pp-signin">SIGN IN</Link>
      </nav>
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <p className="font-mono text-xs tracking-[0.25em] text-accent2">THE OFFICIAL PLAYBOOK</p>
        <h1 className="text-4xl sm:text-5xl font-extrabold mt-3">Bank-tested strategies for each reason code and issuer.</h1>
        <p className="text-white/60 mt-4 max-w-3xl mb-10">Your terms of service are defensive. A chargeback is decided between the cardholder and their bank. This is how you make "all sales final" hold up.</p>
        <PlaybookContent />
      </div>
    </div>
  );
}
